/**
 * Checkout Protection Interceptor
 * 
 * Global interceptor that prevents BACKEND cart rebuilds during checkout/approval.
 * Frontend cart operations are ALLOWED - users can still modify their cart.
 * Only BACKEND-initiated cart rebuilds from draft orders are blocked.
 * 
 * @author Stroetmann Development Team
 * @version 2.0.0
 */

(function() {
  'use strict';
  
  // Store original fetch
  const originalFetch = window.fetch;
  
  // NOTE: We now only block add-to-cart from PDP/Collection pages during checkout
  // All other operations are allowed to ensure checkout/approval works correctly
  
  /**
   * Check if backend rebuild protection is active
   */
  function isBackendRebuildProtectionActive() {
    // Check ALL protection flags
    return window.BLOCK_BACKEND_CART_REBUILDS === true ||
           localStorage.getItem('checkout_protection_active') === 'true' ||
           localStorage.getItem('checkout_started') === 'true' ||
           document.body.dataset.checkoutActive === 'true' ||
           (window.sharedCartService && window.sharedCartService.isCheckoutStarted === true);
  }
  
  /**
   * Check if this is an add-to-cart from PDP/Collection (should be blocked during checkout)
   */
  function isAddToCartFromPDP(url, options) {
    const urlString = url.toString().toLowerCase();
    
    // Check if it's a cart/add operation
    if (!urlString.includes('/cart/add')) {
      return false;
    }
    
    // Check referrer or source to determine if from PDP/Collection
    // This would need to be set by the PDP/Collection add to cart buttons
    const isFromPDP = document.querySelector('[data-adding-from-pdp]') !== null;
    const isFromCollection = document.querySelector('[data-adding-from-collection]') !== null;
    
    return isFromPDP || isFromCollection;
  }
  
  /**
   * Override global fetch to intercept cart operations during checkout
   */
  window.fetch = function(url, options = {}) {
    const urlString = url.toString().toLowerCase();
    
    // During checkout, only block add-to-cart from PDP/Collection pages
    if (isBackendRebuildProtectionActive()) {
      // Check if this is an add-to-cart from PDP/Collection
      if (isAddToCartFromPDP(url, options)) {
        console.warn('⛔ CHECKOUT PROTECTION: Blocked add-to-cart from PDP/Collection during checkout');
        console.warn('   Blocked URL:', url);
        console.warn('   Reason: Cannot add new items during checkout process');
        
        // Show user-friendly message
        if (window.showCartNotification) {
          window.showCartNotification('Cannot add items during checkout. Please complete or cancel your current order first.', 'error');
        }
        
        return Promise.reject(new Error('Cannot add items during checkout process'));
      }
    }
    
    // Allow ALL other operations including:
    // - Cart updates during approval
    // - Hash checks
    // - Stock checks  
    // - Backend operations needed for checkout
    return originalFetch.call(this, url, options);
  };
  
  /**
   * Emergency stop function - can be called from console
   */
  window.EMERGENCY_STOP_BACKEND_REBUILDS = function(reason = 'manual') {
    console.error('🚨 EMERGENCY STOP: Blocking backend cart rebuilds');
    console.error('   Reason:', reason);
    
    // Set ALL protection flags
    window.BLOCK_BACKEND_CART_REBUILDS = true;
    localStorage.setItem('checkout_protection_active', 'true');
    localStorage.setItem('checkout_started', 'true');
    localStorage.setItem('emergency_stop_active', 'true');
    localStorage.setItem('emergency_stop_reason', reason);
    localStorage.setItem('emergency_stop_timestamp', Date.now().toString());
    document.body.dataset.checkoutActive = 'true';
    document.body.dataset.cartLocked = 'true';
    document.body.dataset.emergencyStop = 'true';
    
    // Stop all background operations
    if (window.cartHashMonitor) {
      window.cartHashMonitor.stop();
      console.log('   ✓ Cart hash monitor stopped');
    }
    
    if (window.smartCartSync) {
      window.smartCartSync.isProcessing = true; // Block sync
      console.log('   ✓ Smart cart sync blocked');
    }
    
    if (window.sharedCartService) {
      window.sharedCartService.isCheckoutStarted = true;
      console.log('   ✓ Shared cart service blocked');
    }
    
    // Clear all timeouts and intervals
    let clearedCount = 0;
    for (let i = 1; i < 99999; i++) {
      if (window.clearTimeout(i)) clearedCount++;
      if (window.clearInterval(i)) clearedCount++;
    }
    console.log(`   ✓ Cleared ${clearedCount} timers/intervals`);
    
    // Dispatch emergency stop event
    document.dispatchEvent(new CustomEvent('checkout:emergency:stop', {
      detail: { reason, timestamp: Date.now() }
    }));
    
    console.log('🚨 Emergency stop complete - backend cart rebuilds blocked');
  };
  
  /**
   * Clear emergency stop - can be called from console
   */
  window.CLEAR_EMERGENCY_STOP = function() {
    console.log('🔓 Clearing emergency stop...');
    
    // Clear all protection flags
    window.BLOCK_BACKEND_CART_REBUILDS = false;
    localStorage.removeItem('checkout_protection_active');
    localStorage.removeItem('checkout_started');
    localStorage.removeItem('emergency_stop_active');
    localStorage.removeItem('emergency_stop_reason');
    localStorage.removeItem('emergency_stop_timestamp');
    localStorage.removeItem('checkoutId');
    localStorage.removeItem('checkout_lock_timestamp');
    document.body.dataset.checkoutActive = 'false';
    document.body.dataset.cartLocked = 'false';
    document.body.dataset.emergencyStop = 'false';
    
    // Re-enable services
    if (window.sharedCartService) {
      window.sharedCartService.isCheckoutStarted = false;
      console.log('   ✓ Shared cart service re-enabled');
    }
    
    if (window.smartCartSync) {
      window.smartCartSync.isProcessing = false;
      console.log('   ✓ Smart cart sync re-enabled');
    }
    
    // Restart cart hash monitor after delay
    if (window.cartHashMonitor && window.customer) {
      setTimeout(() => {
        window.cartHashMonitor.start();
        console.log('   ✓ Cart hash monitor restarted');
      }, 2000);
    }
    
    // Dispatch clear event
    document.dispatchEvent(new CustomEvent('checkout:emergency:cleared', {
      detail: { timestamp: Date.now() }
    }));
    
    console.log('✅ Emergency stop cleared - cart operations re-enabled');
  };
  
  /**
   * Check protection status - can be called from console
   */
  window.CHECK_CHECKOUT_PROTECTION = function() {
    const status = {
      isActive: isBackendRebuildProtectionActive(),
      blockBackendRebuilds: window.BLOCK_BACKEND_CART_REBUILDS,
      localStorage: {
        protectionActive: localStorage.getItem('checkout_protection_active'),
        checkoutStarted: localStorage.getItem('checkout_started'),
        checkoutId: localStorage.getItem('checkoutId'),
        emergencyStop: localStorage.getItem('emergency_stop_active')
      },
      bodyDataset: {
        checkoutActive: document.body.dataset.checkoutActive,
        cartLocked: document.body.dataset.cartLocked,
        emergencyStop: document.body.dataset.emergencyStop
      },
      services: {
        sharedCartService: window.sharedCartService?.isCheckoutStarted,
        smartCartSync: window.smartCartSync?.isProcessing,
        cartHashMonitor: window.cartHashMonitor?.isRunning
      }
    };
    
    console.log('🔒 Checkout Protection Status:', status);
    return status;
  };
  
  // Log initialization
  console.log('✅ Checkout Protection Interceptor initialized');
  console.log('   Only PDP/Collection add-to-cart blocked during checkout');
  console.log('   All other operations: ALLOWED during checkout');
  console.log('   Console commands available:');
  console.log('   - EMERGENCY_STOP_BACKEND_REBUILDS() - Block backend cart rebuilds');
  console.log('   - CLEAR_EMERGENCY_STOP() - Clear emergency stop');
  console.log('   - CHECK_CHECKOUT_PROTECTION() - Check protection status');
  
})();